import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { from as observableFrom, of } from 'rxjs';
import { catchError, distinct, filter, map, mergeMap, toArray } from 'rxjs/operators';

import { Policy } from '@amfam/policy/models/lib/models/policy';
import { LoadAgentAction } from '@amfam/shared/utility/agent/lib/+state/agent.actions';
import { Agent, initialAgent } from '@amfam/shared/utility/agent/lib/models/agent.model';

import { PolicyService } from '../../services/policy.service';
import { SummariesAction, SummariesActionTypes } from '../summaries/summaries.actions';
import {
  LoadPolicies,
  PoliciesLoadComplete,
  PoliciesLoadSuccess,
  PoliciesRawLoadSuccess,
  PoliciesSetSelected,
  PoliciesSetSelectedError,
  PoliciesSetSelectedSuccess,
  PolicyLoadError
} from './policies.actions';
import { selectPolicyEntities } from './policies.selectors';

export const loading = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(SummariesActionTypes.SummariesLoadSuccessType),
      map((action: SummariesAction) => {
        return action.payload;
      }),
      // Do not call the policy details load if we do not have policy summaries
      filter(policySummaries => !!(policySummaries?.length ?? 0)),
      mergeMap(payload => {
        return of(LoadPolicies(payload));
      })
    );
  },
  { functional: true }
);

export const load = createEffect(
  (actions$ = inject(Actions), store = inject(Store), policyService = inject(PolicyService)) => {
    return actions$.pipe(
      ofType(LoadPolicies),
      map(action => action.payload),
      mergeMap(policySummaries => {
        // TODO emit an action that an individual policy load failed so we can show that on the UI.
        return observableFrom(policySummaries).pipe(
          // Don't want to make policy details calls when it is an operator only policy or when the user is supposed to be taken to OLS for policy details
          filter(summary => !(summary.operator || summary.takeToOLS)),
          mergeMap(policySummary =>
            policyService.getPolicyDetails(policySummary).pipe(
              /**
               * AS: This catch is important to ensure other call in the mergemap succeed.
               * as of now we do not know what to do with the information so we retrun the response as
               * undefined.
               */
              catchError(() => {
                store.dispatch(PolicyLoadError({ policyNumber: policySummary.policyNumber }));
                return of(undefined);
              })
            )
          ),
          toArray()
        );
      }),
      mergeMap(policies => {
        const returnErrArray: Action[] = [];
        /**
         * If we do not have any policies loaded via the inner subscription of mergeMap then
         * thow the error otherwise treat this as success and load the policy store.
         */
        if (policies.some(policy => policy != null)) {
          const filtered = policies.filter(
            (policy): policy is Policy => policy != null && !!policy.policyNumber
          );
          returnErrArray.push(
            PoliciesLoadSuccess(filtered),
            PoliciesRawLoadSuccess(filtered),
            PoliciesLoadComplete()
          );
        } else {
          returnErrArray.push(PolicyLoadError());
        }
        return returnErrArray;
      }),
      catchError(error => {
        return of(PolicyLoadError(error?.message ?? ''));
      })
    );
  },
  { functional: true }
);

export const loadPolicy = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(PoliciesLoadSuccess),
      map(action => action.payload),
      mergeMap(payload => {
        return observableFrom(
          (payload as Policy[])
            .filter(
              p =>
                p.policyPartnerId === 'AFI' &&
                !['0', '', null, undefined].includes(p.assignedProducer?.producerIdNum)
            )
            .map(policy => policy.assignedProducer?.producerIdNum)
        );
      }),
      // prevent multiple loads of the same producer
      distinct(),
      map(producerId => ({ ...initialAgent, id: producerId })),
      map((agent: Agent) => LoadAgentAction({ payload: agent }))
    );
  },
  { functional: true }
);

export const setselected = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PoliciesSetSelected),
      map(action => action.payload),
      filter(policyNumber => !!policyNumber),
      concatLatestFrom(() => store.select(selectPolicyEntities)),
      mergeMap(([policyNumber, policies]) => {
        const policyNumbers = Object.keys(policies);
        const matchedPolicyNumber = policyNumbers.find(polNumber =>
          polNumber.includes(policyNumber)
        );
        const policyFound = !!policies[matchedPolicyNumber ?? ''];
        if (policyFound) {
          return of(
            PoliciesSetSelectedSuccess({
              policyNumber: matchedPolicyNumber ?? ''
            })
          );
        } else {
          return of(PoliciesSetSelectedError({ policyNumber }));
        }
      })
    );
  },
  { functional: true }
);
